<script setup lang="ts">
  import type {
    NtpNewsEvent,
    NtpNewsTileType,
  } from '../../composables/analytics/types'
  import { useRedirectActions } from '../../composables/useRedirectActions'
  import { useAnalytics } from '../../composables/analytics/useAnalytics'
  import { useFormattedTimeDifference } from '../../composables/date/useFormattedTimeDifference'
  import { useExpandedPublishedDate } from '../../utils/date/useExpandedPublishedDate'
  import { calculateStoryAge } from '../../composables/date/calculateStoryAge'
  import type { NewsCategory } from '../../server/models/news/news-article'
  import Button from '../Buttons/Button.vue'
  import Icon from '../Icon/Icon.vue'
  import { getArticleStyleVariants } from './articleStyleVariants'
  import { userEngagementStore } from '../../store/userData/UserEngagementStore'
  import { useIntersectionObserver, useWindowSize } from '@vueuse/core'
  import {
    registerVisibleContent,
    setVisibleContent,
    unregisterVisibleContent,
  } from '../../composables/useVisibleContent'
  import { getQuery } from 'ufo'
  import { getSortOrderById } from '../../composables/analytics/useFetchedContent'
  import { calculateAutoLayoutColumnsForWidth } from '../../utils/layout/calculateAutoLayoutColumnsForWidth'

  import { useViewedItems } from '../../composables/useViewedItems'
  import type { DesignVariant } from '../../store/feature-flags/useLayoutSettingsStore'
  import {
    REORDER_STRATEGIES,
    reorderNewsHeadlinesForCategory,
    type ReorderStrategy,
  } from '../../composables/data-fetching/useNewsArticles'
  import type { Client } from '@openfeature/web-sdk'

  function pickReorderStrategy(client: Client): ReorderStrategy | null {
    const value = client.getStringValue('contentOrder', 'inherit')
    return REORDER_STRATEGIES.find((s) => s === value) ?? null
  }

  export interface ArticleCardProps {
    design?: DesignVariant
    variant?:
      | 'default'
      | 'yolo-l'
      | 'yolo-xl'
      | 'yolo-xxl'
      | 'yolo-full'
      | 'transparent'
      | 'title-msn'
      | 'subtle-greybg'
      | 'video-s'
      | 'video-l'
      | '2x2'
      | 'hero'
    size?: 'tiny' | 'default' | 'large'
    type?: 'video' | 'game' | undefined
    tileType?: NtpNewsTileType
    tilePosition?: number

    thumbnailUrl?: string
    headline?: string
    subtitle?: string
    publisherName?: string
    publisherIconUrl?: string
    publishedDate?: string | Date
    providerName?: string

    id?: string
    articleUrl?: string // original Article URL
    articleCategory?: NewsCategory | string
    ad?: boolean
    showPublishedDate?: boolean
    testSectionPlacement?: string
    recommendationSource?: string
    // Thumbnail loading hints; the layout sets them by card position.
    imageLoading?: 'eager' | 'lazy'
    imageFetchPriority?: 'high' | 'low' | 'auto'
  }

  const { t } = useI18n()
  const emit = defineEmits<{
    (e: 'click', props: ArticleCardProps): void
  }>()

  const props = withDefaults(defineProps<ArticleCardProps>(), {
    design: 'google',
    variant: 'default',
    size: 'default',
    tileType: 'title',
    tilePosition: undefined,

    ad: false,
    thumbnailUrl: '',
    headline: '',
    subtitle: '',
    publisherName: '',
    providerName: '',
    publisherIconUrl: '',
    publishedDate: '',
    articleCategory: '',
    articleUrl: '',
    id: '',
    type: undefined,
    showPublishedDate: true,
    testSectionPlacement: undefined,
    recommendationSource: undefined,
    imageLoading: undefined,
    imageFetchPriority: undefined,
  })
  const showCategoryInsteadOfSource = await useShowCategoryInsteadOfSource()
  const shouldReplacePublisherWithCategory = showCategoryInsteadOfSource
  const { selectedCategory } = useSearchParams()
  const selectedTabCategoryFromSummaryUrl = computed(() => {
    const href = getHrefUrl()
    if (!href) return undefined
    const query = getQuery(href)
    const value = query.category
    if (Array.isArray(value)) return value[0]
    return value
  })
  const activeTabCategory = computed(
    () => selectedTabCategoryFromSummaryUrl.value || selectedCategory.value
  )
  const publisherDisplayName = computed(() => {
    if (!shouldReplacePublisherWithCategory.value) return props.publisherName

    const tabCategory = activeTabCategory.value
    if (tabCategory && tabCategory !== 'News' && tabCategory !== 'Discover')
      return tabCategory
    if (props.articleCategory) return props.articleCategory
    return props.publisherName
  })

  const { getNewsArticleUrl } = useRedirectActions()

  const isNativeAd = computed(() => props.ad)

  // Use reactive window size for column count that updates on resize
  const { width } = useWindowSize()
  const columnCount = computed(() =>
    calculateAutoLayoutColumnsForWidth(width.value)
  )

  /**
   * Generates analytics event details from article card props
   * @returns Object containing all analytics fields for tracking
   */
  function getEventDetailsForAnalytics(): NtpNewsEvent {
    // Use sort order from useFetchedContent (API response order) to match the
    // visibleContent array sent in NtpPage_Viewed and NtpMoreNews_Loaded events.
    // Falls back to undefined for articles not in fetched content (e.g., Quartz/Inventory
    // test section articles tracked separately via testSectionContent).
    const currentSortOrder = props.id ? getSortOrderById(props.id) : undefined

    return {
      ntp_news_category: props.articleCategory,
      ntp_news_headline: props.headline,
      ntp_news_provider: props.providerName,
      ntp_news_publisher: props.publisherName,
      ntp_news_tile_style: props.tileType,
      ntp_tile_position: props.tilePosition || undefined,
      ntp_content_type: props.type || 'article',
      ntp_sort_order: currentSortOrder,
      ntp_story_age: props.publishedDate
        ? calculateStoryAge(props.publishedDate)
        : undefined,
      ntp_article_id: props.id || undefined,
      ntp_publisher_url_link: props.articleUrl || undefined,
      column_count: columnCount.value,
      ntp_test_section_placement: props.testSectionPlacement || undefined,
      ntp_recommendation_source: props.recommendationSource || undefined,
    }
  }

  const { track } = useAnalytics()
  const userEngagement = userEngagementStore()
  const { addViewedItem } = useViewedItems()
  const { client: openFeatureClient } = await useOpenFeature()
  const reorderStrategy = pickReorderStrategy(openFeatureClient)
  const absoluteDateAfterHours = openFeatureClient.getNumberValue(
    'absolutePublishedDateAfterHours',
    0
  )
  const formattedPublishedDate = computed(() =>
    absoluteDateAfterHours > 0 &&
    props.publishedDate &&
    calculateStoryAge(props.publishedDate) >= absoluteDateAfterHours
      ? useExpandedPublishedDate(props.publishedDate)
      : useFormattedTimeDifference(props.publishedDate)
  )

  /**
   * Visibility tracking setup for article cards
   *
   * Uses IntersectionObserver to:
   * 1. Mark articles as viewed when 50%+ visible
   * 2. Track visible content for analytics (when sortOrder is set)
   *
   * The observer automatically cleans up on component unmount.
   */
  const cardRef = ref<HTMLElement | null>(null)
  const sortOrder = ref<number | null>(null)
  const hasBeenMarkedViewed = ref(false)

  // Intersection observer monitors when article card enters/exits viewport
  useIntersectionObserver(
    cardRef,
    (entries) => {
      // Guard against empty entries array
      if (!entries.length) return

      const entry = entries[0]
      const isIntersecting = entry.isIntersecting
      const intersectionRatio = entry.intersectionRatio

      // Only count as visible if at least 50% in viewport
      const isVisible = isIntersecting && intersectionRatio >= 0.5

      if (props.id && !props.ad) {
        // Track as viewed item (independent of sortOrder)
        // Only mark once to avoid repeated localStorage access
        if (isVisible && !hasBeenMarkedViewed.value) {
          addViewedItem(props.id)
          hasBeenMarkedViewed.value = true
        }

        // Track visible content for analytics (requires sortOrder to be set)
        if (sortOrder.value !== null && sortOrder.value >= 0) {
          setVisibleContent(props.id, isVisible)
        }
      }
    },
    {
      threshold: [0, 0.5, 1.0], // Track 0%, 50%, and 100% visibility
    }
  )

  /**
   * Handles click events on article cards
   * - Tracks user engagement
   * - Prevents default link behavior
   * - Emits click event with props
   * - Opens article URL via redirect handler
   *
   * @param event - Mouse click event
   */
  function handleClick(event: MouseEvent) {
    if (isNativeAd.value) {
      return
    }
    userEngagement.engage('article')
    event.preventDefault()
    emit('click', props)

    /**
     * Mark a clicked article as 'viewed' for user engagement tracking
     */
    if (props.id && !hasBeenMarkedViewed.value) {
      addViewedItem(props.id)
      hasBeenMarkedViewed.value = true
    }

    if (props.id) {
      track('NtpNewsTile_Clicked', getEventDetailsForAnalytics())
      if (reorderStrategy) {
        const reordered = reorderNewsHeadlinesForCategory(
          selectedCategory.value,
          reorderStrategy
        )
        if (reordered.count > 0) {
          track('NtpNewsTile_ContentShuffled', {
            ntp_news_category: reordered.category,
            ntp_shuffled_count: reordered.count,
            ntp_content_order: reorderStrategy,
          })
        }
      }
    }
  }

  function getHrefUrl() {
    if (isNativeAd.value) return
    return getNewsArticleUrl(props)
  }

  /**
   * Register or re-register the article for visibility tracking.
   * Called on mount and when props.id changes (component recycling).
   */
  function registerArticle() {
    if (props.id && !props.ad) {
      const summaryUrl = getNewsArticleUrl(props)
      sortOrder.value = registerVisibleContent(props.id, {
        headline: props.headline,
        publisher: props.publisherName,
        url: props.articleUrl, // External article URL
        summary_url: summaryUrl, // OneNews summary page URL
        category: props.articleCategory,
        type: props.type || 'article',
        publishedDate: props.publishedDate,
      })
    }
  }

  onMounted(() => {
    // Register for visibility tracking (only for non-ad articles with IDs)
    registerArticle()
    if (!props.ad && props.thumbnailUrl) {
      track('NtpNewsTile_Rendered', getEventDetailsForAnalytics())
    }
  })

  // Watch for props.id changes. This handles Vue component recycling
  // When Vue reuses a component instance with different props, onMounted doesn't fire
  // but we need to update sortOrder for the new article
  watch(
    () => props.id,
    (newId, oldId) => {
      if (newId !== oldId) {
        if (oldId) {
          unregisterVisibleContent(oldId)
        }

        // Only register new article if newId exists and not an ad
        if (newId && !props.ad) {
          registerArticle()
        }
      }
    }
  )

  onUnmounted(() => {
    // Unregister from visibility tracking
    if (props.id && !props.ad) {
      unregisterVisibleContent(props.id)
      sortOrder.value = null
    }
  })

  const style = computed(() => getArticleStyleVariants(props))
  const showQuartzBadge = computed(
    () => props.design === 'quartz' && !!props.articleCategory
  )
</script>
<!--
  To prevent ads from rendering more than once on the same tile, we use the key property.
  When the key changes, it triggers a component re-render, effectively refreshing the content
  and ensuring that content directly inserted by AY and its associated APIs is removed.
  We use the "class" attribute that comes from the feature flags as the key, since it
  distinguishes each ad placement.
-->
<template>
  <a
    ref="cardRef"
    :key="props.ad ? String($attrs.class) : 'news-tile'"
    :href="getHrefUrl()"
    target="_blank"
    class="relative transition duration-300"
    :class="style.wrapper"
    role="article"
    :aria-label="props.ad ? 'Partner Content' : headline"
    @click="handleClick"
  >
    <NuxtImg
      v-if="!isNativeAd && variant !== 'hero' && style.image"
      class="transition duration-300"
      role="img"
      :aria-label="`Image for ${props.ad ? 'Partner Content' : headline}`"
      :class="style.image"
      :src="thumbnailUrl"
      :loading="imageLoading"
      :fetchpriority="imageFetchPriority"
      referrerpolicy="no-referrer"
    />
    <div
      v-if="type === 'video'"
      :class="[
        'flex justify-center items-center absolute inset-0 z-10 top-20',
        style.playButton || 'h-5',
      ]"
    >
      <Icon
        icon="play-button"
        :classes="
          variant === 'video-s' || variant === 'video-l'
            ? ['h-[40px]', 'w-[40px]']
            : []
        "
      />
    </div>
    <div
      v-if="
        props.design === 'msn' &&
        props.variant === 'transparent' &&
        (props.size === 'default' || props.size === 'large')
      "
      class="absolute w-full bottom-0 h-[180px] bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.5)_46.02%,rgba(0,0,0,0.5)_100%)] rounded-lg"
    />
    <template
      v-if="
        !isNativeAd && ['default', 'video-s', 'title-msn'].includes(variant)
      "
    >
      <div v-if="showQuartzBadge" :class="style.category">
        <Badge :label="String(articleCategory)" tone="dark-blue" />
        <Typography
          v-if="props.showPublishedDate"
          :class="style.publisher?.text"
        >
          {{ formattedPublishedDate }}
        </Typography>
      </div>
      <Typography
        v-if="style.headline"
        class="transition duration-500"
        :class="style.headline"
      >
        {{ headline }}
      </Typography>

      <Typography
        v-if="style.subtitle && subtitle"
        class="transition duration-500"
        :class="style.subtitle"
      >
        {{ subtitle }}
      </Typography>

      <div
        v-if="
          !showQuartzBadge &&
          style.publisher &&
          (publisherDisplayName || props.showPublishedDate)
        "
        :class="style.publisher.wrapper"
      >
        <NuxtImg
          v-if="style.publisher.icon && !shouldReplacePublisherWithCategory"
          :class="style.publisher.icon"
          :src="publisherIconUrl"
          referrerpolicy="no-referrer"
        />
        <div v-if="style.publisher" class="flex flex-row gap-1">
          <Typography v-if="publisherDisplayName" :class="style.publisher.text">
            {{ publisherDisplayName }}
          </Typography>
          <Typography
            v-if="publisherDisplayName && props.showPublishedDate"
            :class="style.publisher.text"
          >
            ·
          </Typography>
          <Typography
            v-if="props.showPublishedDate"
            :class="style.publisher.text"
          >
            {{ formattedPublishedDate }}
          </Typography>
        </div>
      </div>
    </template>

    <div
      v-if="
        (!isNativeAd && variant?.startsWith('yolo')) ||
        variant === 'transparent' ||
        variant === '2x2'
      "
      class="flex flex-col self-center transition duration-300"
    >
      <Typography
        v-if="props.headline"
        class="transition duration-500"
        :class="style.headline"
      >
        {{ headline }}
      </Typography>
      <div
        :class="{
          'flex flex-row gap-10 px-4 mb-4 pb-4 items-start': variant === '2x2',
          'flex flex-col transition duration-300': variant !== '2x2',
          'order-4': design === 'msn',
        }"
      >
        <Typography
          v-if="style.subtitle"
          class="flex-1"
          :class="style.subtitle"
        >
          {{ subtitle }}
        </Typography>
        <Button
          v-if="style.button"
          :class="style.button"
          size="small"
          label-font-variant="text-xs"
          @click="handleClick"
        >
          {{ t('Read more') }}
        </Button>
      </div>
      <div v-if="style.publisher" :class="style.publisher.wrapper">
        <NuxtImg
          v-if="style.publisher.icon && !shouldReplacePublisherWithCategory"
          class="transition duration-300"
          :class="style.publisher.icon"
          :src="publisherIconUrl"
          referrerpolicy="no-referrer"
        />
        <div v-if="!style.publisher.ad" class="flex flex-row gap-1">
          <Typography :class="style.publisher.text">
            {{ publisherDisplayName }}
          </Typography>
          <Typography
            v-if="props.showPublishedDate"
            :class="style.publisher.text"
          >
            ·
          </Typography>
          <Typography
            v-if="props.showPublishedDate"
            :class="style.publisher.text"
          >
            {{ formattedPublishedDate }}
          </Typography>
        </div>
        <div v-if="style.publisher.ad" class="flex flex-row gap-1">
          <Typography :class="style.publisher.text">
            {{ publisherDisplayName }}
          </Typography>
        </div>
      </div>
    </div>
    <div
      v-if="!isNativeAd && variant === 'hero'"
      class="flex flex-col self-center transition duration-300"
    >
      <Typography
        v-if="props.headline"
        class="transition duration-500"
        :class="style.headline"
      >
        {{ headline }}
      </Typography>
      <NuxtImg
        v-if="!isNativeAd && variant === 'hero' && style.image"
        class="transition duration-300"
        role="img"
        :aria-label="`Image for ${props.ad ? 'Partner Content' : headline}`"
        :class="style.image"
        :src="thumbnailUrl"
        :loading="imageLoading"
        :fetchpriority="imageFetchPriority"
        referrerpolicy="no-referrer"
      />
      <div v-if="style.publisher" :class="style.publisher.wrapper">
        <NuxtImg
          v-if="style.publisher.icon && !shouldReplacePublisherWithCategory"
          class="transition duration-300"
          :class="style.publisher.icon"
          :src="publisherIconUrl"
          referrerpolicy="no-referrer"
        />
        <div v-if="!style.publisher.ad" class="flex flex-row gap-1">
          <Typography :class="style.publisher.text">
            {{ publisherDisplayName }}
          </Typography>
          <Typography
            v-if="props.showPublishedDate"
            :class="style.publisher.text"
          >
            ·
          </Typography>
          <Typography
            v-if="props.showPublishedDate"
            :class="style.publisher.text"
          >
            {{ formattedPublishedDate }}
          </Typography>
        </div>
        <div v-if="style.publisher.ad" class="flex flex-row gap-1">
          <Typography :class="style.publisher.text">
            {{ publisherDisplayName }}
          </Typography>
        </div>
      </div>
      <div
        :class="{
          'flex flex-row gap-10 px-4 mb-4 items-center': variant === 'hero',
        }"
      >
        <Typography
          v-if="style.subtitle"
          class="flex-1"
          :class="style.subtitle"
        >
          {{ subtitle }}
        </Typography>
        <Button
          v-if="style.button"
          :class="style.button"
          size="small"
          label-font-variant="text-xs"
          @click="handleClick"
        >
          {{ t('Read more') }}
        </Button>
      </div>
    </div>
  </a>
</template>
